import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Shield, Users, BookOpen, Settings } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

const ROLE_PERMISSIONS = {
  admin: {
    label: "Administrador",
    icon: Settings,
    color: "#4B2E14",
    permissions: [
      "manage_employees",
      "create_training",
      "manage_training",
      "manage_training_content",
      "manage_announcements",
      "view_reports",
      "view_progress",
      "system_settings",
      "issue_certificates",
      "modify_all"
    ]
  },
  supervisor: {
    label: "Supervisor",
    icon: Users,
    color: "#C6A136",
    permissions: [
      "view_employees",
      "manage_training_assignments",
      "view_progress",
      "manage_announcements"
    ]
  },
  trainer: {
    label: "Capacitador",
    icon: BookOpen,
    color: "#FFD33B",
    permissions: [
      "create_training",
      "manage_training_content",
      "view_progress",
      "issue_certificates"
    ]
  },
  employee: {
    label: "Empleado",
    icon: Shield,
    color: "#E2C77A",
    permissions: [
      "view_training",
      "complete_training",
      "view_certificates",
      "view_announcements"
    ]
  }
};

export function useRolePermissions() {
  const { employee } = useAuth();

  const hasPermission = (permission) => {
    if (!employee) return false;
    const userRole = employee.role || 'employee';
    return ROLE_PERMISSIONS[userRole]?.permissions.includes(permission) || false;
  };

  const hasRole = (role) => {
    if (!employee) return false;
    return employee.role === role;
  };

  const hasAnyRole = (roles) => {
    if (!employee) return false;
    return roles.includes(employee.role);
  };

  const getRoleInfo = (role) => {
    return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.employee;
  };

  const getCurrentUserRole = () => {
    if (!employee) return ROLE_PERMISSIONS.employee;
    return getRoleInfo(employee.role || 'employee');
  };

  return {
    hasPermission,
    hasRole,
    hasAnyRole,
    getRoleInfo,
    getCurrentUserRole,
    employee,
    ROLE_PERMISSIONS
  };
}

export default function RoleGuard({ children, permission, role, roles, fallback }) {
  const { hasPermission, hasRole, hasAnyRole, employee } = useRolePermissions();

  if (!employee) {
    return <Navigate to="/login" replace />;
  }

  let hasAccess = true;

  if (permission && !hasPermission(permission)) {
    hasAccess = false;
  }

  if (role && !hasRole(role)) {
    hasAccess = false;
  }

  if (roles && !hasAnyRole(roles)) {
    hasAccess = false;
  }

  if (!hasAccess) {
    return fallback || (
      <div className="flex items-center justify-center min-h-screen bg-[#FBF9F3]">
        <Alert className="max-w-md border-[#C6A136] bg-[#FFF8E1]">
          <Shield className="h-4 w-4 text-[#C6A136]" />
          <AlertTitle className="text-[#4B2E14]">Acceso Restringido</AlertTitle>
          <AlertDescription className="text-[#4B2E14]/80">
            No tienes permisos para acceder a esta página. Si crees que esto es un error, contacta con tu administrador.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}

export function AdminGuard({ children }) {
  return <RoleGuard roles={['admin']}>{children}</RoleGuard>;
}

export function TrainerGuard({ children }) {
  return <RoleGuard roles={['admin', 'trainer']}>{children}</RoleGuard>;
}
